import { format } from 'date-fns';
import { AxiosResponse } from 'axios';
import { axios } from "../../config/axios";

export const reportsAPI = {
  /**
   * Fetches all event types for event dropdown
   */
  fetchImpactReportEventTypes() {
    return axios.get(`/api/v3/reports/options?report[type]=Reports::ImpactReport`)
      .then((resp) => resp.data.event_types);
  },
  /**
   * Fetches all available KPIs for kpi dropdown
   */
  fetchKpis() {
    return axios.get('/api/v3/reports/assessments?report[type]=Reports::ImpactReport')
      .then((resp) => resp.data);
  },
  /**
   * Creates a new report based on selected criteria
   * @param {Object} options - an object containing all the required params when running a new report
   */
  postImpactReportJob(options) {
    return axios.post(`/api/v3/reports`, options)
      .then((resp) => resp.data.report);
  },
  /**
   * Fetches a report by id
   * @param {number} reportId - report id
   */
  fetchReport(reportId) {
    return axios.get(`/api/v3/reports/${reportId}`)
      .then((resp) => resp.data);
  },
  /**
   * Generates a downloadable report
   * @param {number} reportId - report id
   */
  generateReport(reportId) {
    return axios.post(`/api/v3/reports/${reportId}/generate_export`)
      .then((resp) => resp.data);
  },
  /**
   * Creates a new group, used to populate the merchantSegmentId in createCampaign()
   * @param {number} reportId - report id
   * @param {boolean} variant - dictates if a header column is qualified or unqualified
   * @param {string} title - name given to a new group
   */
  createSegment(reportId, variant, title) {
    return axios.post(`/api/v3/reports/${reportId}/create_segment?variant=${variant}&title=${title}`)
      .then((resp) => resp.data);
  },
  /**
   * Creates campaign, populates with selected options and returns campaign id to redirect to new campaign page
   * @param {number} merchantSegmentId - merchant id
   */
  async createCampaign(merchantSegmentId) {
    const { data } = await axios.post(`/api/v3/campaigns`)
    await axios.patch(`/api/v3/campaigns/${data.campaign.id}/update_groups?campaign[merchant_segment_ids][]=${merchantSegmentId}`)
    return data.campaign.id
  },
  /**
   * Fetches reports with the status of 'in progress' for the impact report index page
   */
  fetchInProgress() {
    return axios.get(`/api/v3/reports`, {
      params: {
        'report[type]': 'Reports::ImpactReport',
        page: 1,
        per_page: 100,
        'report[state]': ['unstarted', 'running']
      }
    }).then((resp) => resp.data);
  },
  /**
   * Fetches reports with the status of 'completed' for the impact report index page
   * @param {number} pageNumber - page number for pagination
   */
  fetchCompleted(pageNumber) {
    return axios.get(`/api/v3/reports`, {
      params: {
        'report[type]': 'Reports::ImpactReport',
        page: pageNumber + 1,
        per_page: 10,
        'report[state][]': 'completed'
      }
    }).then((resp) => resp.data);
  },
  /**
   * Deletes report with the given reportId.
   * @param {number} reportId - id of the report
   */
  deleteReport(reportId) {
    return axios.delete(`/api/v3/reports/${reportId}`).then(resp => resp.data)
  },
  /**
   * Fetches all event types for inquiry dropdown
   */
   fetchInquiryReportEventTypes() {
    return axios.get(`/api/v3/reports/options?report[type]=Reports::ImpactInquiryReport`)
      .then((resp) => resp.data.inquiry_types);
  },
  /**
   * Fetches all event kpis for inquiry dropdown
   */
  fetchInquiryReportKpis() {
    return axios.get(`/api/v3/reports/options?report[type]=Reports::ImpactInquiryReport`)
      .then((resp) => resp.data.inquiry_kpis);
  },
  /**
   * Fetches inquiry report
   */
  initializeReportJob(options) {
    return axios.post(`/api/v3/reports`, options)
    .then((resp) => resp.data.report);
  },

  /**
   * Сreates a report for the selected time period and type
   * @param {string} type - type of the report
   * @param {Date} startDate - start date
   * @param {Date} endDate - end date
   * @returns {AxiosResponse}
   * Response example:
    {
      report: {
        created_at: "2022-02-08T19:12:22.732Z"
        download_url: null
        estimated_run_time_remaining: null
        export_state: "unstarted"
        id: 1111
        input_data: {
          date_range_min: '2021-11-08',
          date_range_max: '2022-02-08'
        }
        input_data_labels: {
          date_range_min: '2021-11-08',
          date_range_max: '2022-02-08'
        }
        output_data: {
          results: [
            {
              customer: "Firstname Lastname"
              points_earned: 34
              submitted_via: "checkin"
              transaction_total: 39
              visit_date: "022-02-08T19:12:22.732Z"
            }
          ]
        }
        state: "completed"
        type: "Reports::CheckInReport"
      }
    }
   */
  createReport(type, startDate, endDate) {
    let params = {
      report: {
        type,
      }
    }

    if (startDate && endDate) {
      params = {
        report: {
          ...params.report,
          input_data: {
            date_range_min: format(startDate, 'yyyy-MM-dd'),
            date_range_max: format(endDate, 'yyyy-MM-dd'),
          }
        }
      }
    }
    return axios.post('/api/v3/reports', params).then(resp => resp.data);
  },

  /**
  * Returns report data by id and page.
  * @param {string} id - id of report
  * @param {number} page - page to return data
  * @param {object} options - options of report
  * @returns {AxiosResponse}
  * Response example:
    {
      pagination: {
        current_page: 1
        total_count: 222
        total_pages: 20
      }
      report: {
        created_at: "2022-02-08T19:12:22.732Z"
        download_url: null
        estimated_run_time_remaining: null
        export_state: "unstarted"
        id: 141
        input_data: {
          date_range_min: '2021-11-08',
          date_range_max: '2022-02-08'
        }
        input_data_labels: {
          date_range_min: '2021-11-08',
          date_range_max: '2022-02-08'
        }
        output_data: {
          results: [
            {
              customer: "Owen Huels"
              points_earned: 11
              submitted_via: "checkin"
              transaction_total: 11
              visit_date: "2021-12-07T02:54:54.000Z"
            }
          ]
        }
        state: "completed"
        type: "Reports::CheckInReport"
      }
    }
  */
  getReportById(id, page, options = {}) {
    const { columnKey, direction, searchText } = options;
    let params = searchText ? { page, search_term: searchText } : { page };

    if (columnKey && direction) {
      params = {
        ...params,
        sort_column: columnKey,
        sort_direction: direction,
      };
    }
    return axios.get(`/api/v3/reports/${id}`, { params }).then(resp => resp.data);
  },

  /**
  * Generates a report export data by id.
  * @param {string} reportId - id of the report
  * @returns {AxiosResponse}
  * Response example: {}
  */
  generateExportByReportId(reportId) {
    return axios.post(`/api/v3/reports/${reportId}/generate_export`);
  },

  /**
  * Returns info about generating a report with status and a download URL.
  * @param {string} reportId - id of the report
  * @returns {AxiosResponse}
  * Response example:
    {
      report: {
        download_url: null
        export_state: "unstarted"
        id: 111
      }
    }
  */
  getReportDownloadUrl(reportId) {
    return axios.get(`/api/v3/reports/${reportId}/download_info`).then(resp => resp.data);
  }
};
